import Configuration from "./Configuration";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";

export default class HybridizationAtom {
  constructor() {
    this.pOrbitals = [];
    this.dOrbitals = [];
    this.sOrbital = null;
    this.spOrbital = null;

    // P orbitals
    const qx = Quaternion.FromEulerAngles(0, 0, 0);
    const qz = Quaternion.FromEulerAngles(0, Math.PI / 2, 0);
    // Note: FromEulerAngles(0,0,0) is not the same as Identity
    const qy = Quaternion.FromEulerAngles(0, 0, Math.PI / 2);

    this.pOrientations = [qx, qy, qz];
    this.dOrientations = [qx, qz];
  }

  addPOrbital(orbital) {
    this.pOrbitals.push(orbital);
    let quaternionIndexOffset = this.spOrbital ? 1 : 0;

    if (this.spOrbital) {
      if (this.spOrbital.hybridization === Configuration.sp2) {
        quaternionIndexOffset = 2;
      } else if (this.spOrbital.hybridization === Configuration.sp) {
        quaternionIndexOffset = 1;
      }
    }

    for (let i = 0; i < this.pOrbitals.length; i++) {
      if (this.pOrbitals[i].rotationQuaternion) {
        this.pOrbitals[i].rotationQuaternion.copyFrom(this.pOrientations[i + quaternionIndexOffset]);
      } else {
        this.pOrbitals[i].rotationQuaternion = this.pOrientations[i + quaternionIndexOffset].clone();
      }
    }
  }

  addDOrbital(orbital) {
    this.dOrbitals.push(orbital);

    for (let i = 0; i < this.dOrbitals.length; i++) {
      if (this.dOrbitals[i].rotationQuaternion) {
        this.dOrbitals[i].rotationQuaternion.copyFrom(this.dOrientations[i]);
      } else {
        this.dOrbitals[i].rotationQuaternion = this.dOrientations[i].clone();
      }
    }
  }

  addSPOrbital(orbital) {
    this.reset();
    this.spOrbital = orbital;
  }

  canPlaceP() {
    if (this.spOrbital) {
      if (this.spOrbital.hybridization === Configuration.sp3) {
        return false;
      } else if (this.spOrbital.hybridization === Configuration.sp2) {
        return this.pOrbitals.length < 1;
      } else if (this.spOrbital.hybridization === Configuration.sp)
        return this.pOrbitals.length < 2;
    } else {
      return this.pOrbitals.length < 3;
    }
  }

  canPlaceD() {
    if (this.spOrbital)
      return false;

    return this.dOrbitals.length < 2;
  }

  canPlaceS() {
    return !(this.spOrbital) && !(this.sOrbital);
  }

  /**
   * @param {boolean} pickable
   */
  set isPickable(pickable) {
    this.pickable = pickable;
    if (this.sOrbital) {
      this.sOrbital.isPickable = pickable;
    }

    if (this.pOrbitals) {
      this.pOrbitals.forEach(mesh => mesh.isPickable = pickable);
    }

    if (this.dOrbitals) {
      this.dOrbitals.forEach(mesh => mesh.isPickable = pickable);
    }

    if (this.spOrbital) {
      this.spOrbital.isPickable = pickable;
    }
  }

  get isPickable() {
    return this.pickable;
  }

  get configuration() {
    const numPOrbitals = this.pOrbitals ? this.pOrbitals.length : 0;
    const numDOrbitals = this.dOrbitals ? this.dOrbitals.length : 0;
    const numSOrbital = this.sOrbital ? 1 : 0;

    // console.log(`p:${numPOrbitals} d:${numDOrbitals} s:${numSOrbital} sp:${this.spOrbital}`);

    if (this.spOrbital) {
      switch (numPOrbitals) {
        case 0: return this.spOrbital.hybridization;
        case 1: return this.spOrbital.hybridization === Configuration.sp2 ? Configuration.sp2P : Configuration.spP;
        case 2: return Configuration.spPP;
      }
    } else {
      const configs = [
        { s: 0, p: 0, d: 0, c: Configuration.empty },
        { s: 0, p: 0, d: 1, c: Configuration.D },
        { s: 0, p: 0, d: 2, c: Configuration.DD },
        { s: 0, p: 1, d: 0, c: Configuration.P },
        { s: 0, p: 1, d: 1, c: Configuration.PD },
        { s: 0, p: 1, d: 2, c: Configuration.PDD },
        { s: 0, p: 2, d: 0, c: Configuration.PP },
        { s: 0, p: 2, d: 1, c: Configuration.PPD },
        { s: 0, p: 2, d: 2, c: Configuration.PPDD },
        { s: 0, p: 3, d: 0, c: Configuration.PPP },
        { s: 0, p: 3, d: 1, c: Configuration.PPPD },
        { s: 0, p: 3, d: 2, c: Configuration.PPPDD },
        { s: 1, p: 0, d: 0, c: Configuration.S },
        { s: 1, p: 0, d: 1, c: Configuration.SD },
        { s: 1, p: 0, d: 2, c: Configuration.SDD },
        { s: 1, p: 1, d: 0, c: Configuration.SP },
        { s: 1, p: 1, d: 1, c: Configuration.SPD },
        { s: 1, p: 1, d: 2, c: Configuration.SPDD },
        { s: 1, p: 2, d: 0, c: Configuration.SPP },
        { s: 1, p: 2, d: 1, c: Configuration.SPPD },
        { s: 1, p: 2, d: 2, c: Configuration.SPPDD },
        { s: 1, p: 3, d: 0, c: Configuration.SPPP },
        { s: 1, p: 3, d: 1, c: Configuration.SPPPD },
        { s: 1, p: 3, d: 2, c: Configuration.SPPPDD },
      ];

      const found = configs.find(cfg => {
        return cfg.s === numSOrbital && cfg.p === numPOrbitals && cfg.d === numDOrbitals;
      })

      if (found) {
        return found.c;
      }

    }

    return undefined;
  }

  get hybridizableState() {
    // Already hybridized
    if (this.spOrbital)
      return undefined;

    // No s orbital
    if (!this.sOrbital)
      return undefined;

    const numDs = this.dOrbitals ? this.dOrbitals.length : 0;
    const numPs = this.pOrbitals ? this.pOrbitals.length : 0;

    // Need 3 p orbitals before hybridizing with d
    if (numDs > 0 && numPs < 3)
      return undefined;

    if (numDs > 0) {
      switch (numDs) {
        case 1: return Configuration.sp3d;
        case 2: return Configuration.sp3d2
      }
    } else if (numPs) {
      switch (numPs) {
        case 1: return Configuration.sp;
        case 2: return Configuration.sp2;
        case 3: return Configuration.sp3;
      }
    }
    return undefined;
  }

  get hybridizedPCount() {
    let count = 0;
    if (this.spOrbital) {
      if (this.spOrbital.hybridization === Configuration.sp3) {
        count = 3;
      } else if (this.spOrbital.hybridization === Configuration.sp2) {
        count = 2;
      } else if (this.spOrbital.hybridization === Configuration.sp)
        count = 1;
    }

    return count;
  }


  get totalPCount() {
    return this.hybridizedPCount + (this.pOrbitals ? this.pOrbitals.length : 0);
  }


  reset() {
    if (this.pOrbitals) {
      this.pOrbitals.forEach(o => o.dispose());
    }

    if (this.dOrbitals) {
      this.dOrbitals.forEach(o => o.dispose());
    }

    if (this.sOrbital) {
      this.sOrbital.dispose();
    }

    if (this.spOrbital) {
      this.spOrbital.dispose();
    }

    this.pOrbitals = [];
    this.dOrbitals = [];
    this.sOrbital = null;
    this.spOrbital = null;
  }
}